import { FC, ReactNode, useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import { createAppInitSyncActions, createAppInitAsyncActions, AppInitAction } from "./actions"
import { AppInitContext } from "./AppInitContext"
import { AppInitLoader } from "components/ui/AppInitLoader"
import { createAppInitMiddleware } from "./middlewares/createInitMiddleware"
import { initialState, appInitReducer } from "./reducer"

interface AppInitProviderProps {
  children: ReactNode
}

const AppInitProvider: FC<AppInitProviderProps> = ({ children }) => {
  const [state, baseDispatch] = useReducer(appInitReducer, initialState)

  const stateRef = useRef(state)
  stateRef.current = state
  const getState = useCallback(() => stateRef.current, [])

  const middlewareRef = useRef<any>(null);

  // Dispatch avec middleware (utilise une ref pour éviter la dépendance circulaire)
  const dispatch = useCallback(
    (action: AppInitAction) => {
      if (middlewareRef.current) {
        middlewareRef.current(baseDispatch)(action);
      } else {
        baseDispatch(action);
      }
    },
    []
  );


  const syncActions = useMemo(() => createAppInitSyncActions(dispatch), [dispatch])
  const asyncActions = useMemo(() => createAppInitAsyncActions(syncActions), [syncActions])

  const middleware = useMemo(
    () => createAppInitMiddleware(asyncActions, getState),
    [asyncActions, getState]
  );

  middlewareRef.current = middleware;

  useEffect(() => {
    asyncActions.initializeApp()
  }, [asyncActions])

  const value = useMemo(() => ({
    state,
    dispatch,
    ...syncActions,
    ...asyncActions
  }), [state, dispatch, syncActions, asyncActions])

  if (!state.isInitialized || state.isLoading) {
    return <AppInitLoader error={state.error} />
  }

  return (
    <AppInitContext.Provider value={value}>
      {children}
    </AppInitContext.Provider>
  )
}

export default AppInitProvider
